import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { gbp } from "../lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, setQty, removeFromCart, subtotal } = useShop();

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-[80] bg-ink/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-[90] w-full sm:w-[440px] bg-ink-2 border-l border-gold/20 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-gold/15">
              <h2 className="font-serif text-2xl text-ivory">Your Basket</h2>
              <button onClick={() => setCartOpen(false)} data-testid="cart-close" className="text-stone-mist hover:text-gold transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {cart.length === 0 && (
                <div className="text-center pt-16" data-testid="cart-empty">
                  <p className="font-serif text-2xl text-ivory mb-2">Your basket is empty</p>
                  <p className="text-stone-mist text-sm">Discover jewellery inspired by the Isle of Man.</p>
                </div>
              )}
              {cart.map((item) => (
                <div key={item.key} className="flex gap-4" data-testid={`cart-item-${item.key}`}>
                  <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="w-20 h-24 shrink-0 overflow-hidden border border-white/5">
                    {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="text-ivory font-light leading-snug">{item.name}</p>
                    <p className="text-stone-mist text-xs mt-1">
                      {[
                        item.metal && `Metal: ${item.metal}`,
                        item.width && `Width: ${item.width}`,
                        item.size && (item.size.startsWith("£") ? item.size : `Size ${item.size}`),
                        item.chain && `Chain: ${{ included: "Included", standard: 'Standard 18"', superior: 'Superior 18"' }[item.chain] || "Pendant only"}`,
                      ].filter(Boolean).join(" · ")}
                    </p>
                    {item.availability === "made_to_order" && (
                      <p className="text-gold/80 text-[11px] mt-1">Made to Order — {item.lead_time || "approximately 6 weeks"}</p>
                    )}
                    {item.personalisation && <p className="text-stone-mist/70 text-[11px] mt-1 italic">{item.personalisation}</p>}
                    <div className="flex items-center gap-3 mt-3">
                      <button onClick={() => setQty(item.key, item.qty - 1)} data-testid={`cart-dec-${item.key}`} className="w-7 h-7 border border-white/10 text-ivory flex items-center justify-center hover:border-gold transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-mono text-sm text-ivory w-4 text-center">{item.qty}</span>
                      <button onClick={() => setQty(item.key, item.qty + 1)} data-testid={`cart-inc-${item.key}`} className="w-7 h-7 border border-white/10 text-ivory flex items-center justify-center hover:border-gold transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm text-gold">{gbp(item.price * item.qty)}</p>
                    <button onClick={() => removeFromCart(item.key)} data-testid={`cart-remove-${item.key}`} className="text-stone-mist hover:text-red-400 text-xs mt-3 transition-colors">
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t border-gold/15 space-y-4">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-mist">Subtotal</span>
                  <span className="font-mono text-ivory" data-testid="cart-subtotal">{gbp(subtotal)}</span>
                </div>
                <p className="text-xs text-stone-mist">Delivery calculated at checkout. Island weather may occasionally affect postal services.</p>
                <Link
                  to="/checkout"
                  onClick={() => setCartOpen(false)}
                  data-testid="proceed-checkout"
                  className="block text-center bg-gold text-ink py-4 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors duration-300"
                >
                  Proceed to Checkout
                </Link>
                <Link to="/shop/all" onClick={() => setCartOpen(false)} className="block text-center text-gold text-xs uppercase tracking-[0.25em] hover:text-gold-glow transition-colors">
                  Continue Shopping
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
